import { inject, injectable, interfaces } from 'inversify';
import { IEventBus } from '../../../../services/eventBus.service';
import { NotificationLevel, ToastNotification } from '../../../notifications/models/notification.model';
import { INotificationsService } from '../../../notifications/services/notifications.service';
import { WorkspaceTimeZoneUpdatedEvent } from './events/WorkspaceTimeZoneUpdatedEvent';
import { IUpdateWorkspaceTimeZoneService, UpdateWorkspaceTimeZoneRequest } from './services/UpdateWorkspaceTimeZone.service';
import { IUpdateWorkspaceTimeZoneStore } from './UpdateWorkspaceTimeZone.store';

export interface IUpdateWorkspaceTimeZoneBl {
  hideForm(): void;
  selectTimeZone(timeZone: number): void;
  showForm(workspaceId: string, timeZoneOffset: number): void;
  submitFormAsync(): Promise<void>;
}

export namespace IUpdateWorkspaceTimeZoneBl {
  export const $: interfaces.ServiceIdentifier<IUpdateWorkspaceTimeZoneBl> = Symbol('UPDATE_WORKSPACE_TIMEZONE_BL_TYPE');
}

@injectable()
export class UpdateWorkspaceTimeZoneBl implements IUpdateWorkspaceTimeZoneBl {
  private originalTimeZoneOffset: number | undefined;

  constructor(
    @inject(IUpdateWorkspaceTimeZoneStore.$) private readonly store: IUpdateWorkspaceTimeZoneStore,
    @inject(IUpdateWorkspaceTimeZoneService.$) private readonly updateTimeZoneService: IUpdateWorkspaceTimeZoneService,
    @inject(INotificationsService.$) private readonly notifications: INotificationsService,
    @inject(IEventBus.$) private readonly eventBus: IEventBus
  ) {}

  public showForm(workspaceId: string, timeZoneOffset: number): void {
    this.originalTimeZoneOffset = timeZoneOffset;
    this.store.showForm(workspaceId, timeZoneOffset);
  }

  public hideForm(): void {
    this.store.hideForm();
  }

  public selectTimeZone(timeZone: number): void {
    this.store.setSelectedTimeZoneOffset(timeZone);
  }

  public async submitFormAsync(): Promise<void> {
    if (this.store.selectedTimeZoneOffset === this.originalTimeZoneOffset) {
      this.hideForm();
      return;
    }

    try {
      const request = new UpdateWorkspaceTimeZoneRequest(this.store.workspaceId, this.store.selectedTimeZoneOffset);
      await this.updateTimeZoneService.updateWorkspaceTimeZone(request);
      this.eventBus.sendEvent(new WorkspaceTimeZoneUpdatedEvent(this.store.workspaceId, this.store.selectedTimeZoneOffset));
      this.hideForm();
    } catch {
      await this.notifications.push(new ToastNotification(NotificationLevel.ERROR, 'update_timezone_failed'));
    }
  }
}
